import {
  Phone,
  PhoneMissed,
  Clock,
  User,
  MessageCircle,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { FollowUpStatusBadge } from "@/components/followup/FollowUpStatusBadge";

const TYPE_COLORS = {
  Mahallu: "bg-violet-100 text-violet-700",
  Individual: "bg-sky-100 text-sky-700",
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString("en-IN", {
    month: "short",
    day: "numeric",
  });
}

/**
 * CalendarSponsorCard
 * Props:
 *   sponsor       — shaped sponsor from calendar API
 *   isOverdue     — boolean
 *   isToday       — boolean
 *   onLogFollowUp — (sponsor) => void
 *   onPullToWorkspace — (sponsor) => void
 */
export function CalendarSponsorCard({
  sponsor,
  isOverdue = false,
  isToday = false,
  onLogFollowUp,
  onPullToWorkspace,
}) {
  const progress =
    sponsor.amountPromised > 0
      ? Math.min(
          100,
          Math.round((sponsor.paidAmount / sponsor.amountPromised) * 100),
        )
      : 0;

  const whatsappHref = sponsor.sponsorPhone
    ? `https://wa.me/${sponsor.sponsorPhone.replace(/\D/g, "")}`
    : null;

  return (
    <div
      className={cn(
        "rounded-2xl border bg-card shadow-sm transition-transform hover:-translate-y-px select-none",
        isOverdue && "border-red-200 bg-red-50/50",
        isToday && !isOverdue && "border-amber-200 bg-amber-50/30",
      )}
    >
      <div className="p-4 space-y-3">
        {/* ── Header row ── */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-semibold text-sm leading-tight truncate">
              {sponsor.sponsorName}
            </p>
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {sponsor.mahalluName || sponsor.place || "—"}
              {sponsor.chapter?.name ? ` · ${sponsor.chapter.name}` : ""}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span
              className={cn(
                "text-[10px] font-medium rounded-full px-2 py-0.5",
                TYPE_COLORS[sponsor.type] ?? "bg-gray-100 text-gray-600",
              )}
            >
              {sponsor.type}
            </span>
            <FollowUpStatusBadge status={sponsor.followUpStatus} size="xs" />
          </div>
        </div>

        {/* ── Progress bar ── */}
        <div className="space-y-1">
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>₹{(sponsor.paidAmount ?? 0).toLocaleString()} paid</span>
            <span>
              ₹{(sponsor.amountPromised ?? 0).toLocaleString()} pledged
            </span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                progress >= 100
                  ? "bg-green-500"
                  : progress >= 50
                    ? "bg-amber-400"
                    : "bg-red-400",
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* ── Meta row ── */}
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground flex-wrap">
          {sponsor.failedAttempts > 0 && (
            <span className="flex items-center gap-0.5 text-red-500">
              <PhoneMissed className="h-3 w-3" />
              {sponsor.failedAttempts} missed
            </span>
          )}
          {sponsor.lastContactDate && (
            <span className="flex items-center gap-0.5">
              <Clock className="h-3 w-3" />
              Last: {formatDate(sponsor.lastContactDate)}
            </span>
          )}
          {sponsor.assignedTo && (
            <span className="flex items-center gap-0.5">
              <User className="h-3 w-3" />
              {sponsor.assignedTo.name ||
                sponsor.assignedTo.email ||
                "Assigned"}
            </span>
          )}
          {sponsor.sponsorPhone && (
            <span className="flex items-center gap-0.5">
              <Phone className="h-3 w-3" />
              {sponsor.sponsorPhone}
            </span>
          )}
        </div>

        {/* ── Action buttons ── */}
        <div className="flex items-center gap-2 pt-0.5">
          {whatsappHref && (
            <Button
              asChild
              size="sm"
              variant="outline"
              className="h-7 text-xs gap-1.5 text-green-600 border-green-200"
            >
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="h-3 w-3" />
                WhatsApp
              </a>
            </Button>
          )}
          {onLogFollowUp && (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => onLogFollowUp(sponsor)}
            >
              Log Follow-Up
            </Button>
          )}
          {onPullToWorkspace && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs gap-1 ml-auto text-primary"
              onClick={() => onPullToWorkspace(sponsor)}
            >
              Open in Workspace
              <ArrowRight className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
